import { useState, useEffect, useCallback, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type DeviceType = 'ios' | 'android' | 'desktop';

export type BrowserType =
  | 'safari'
  | 'safari-26'      // iOS 26+ Safari (new UI)
  | 'chrome'
  | 'firefox'
  | 'edge'
  | 'samsung'
  | 'opera'
  | 'inapp-facebook'
  | 'inapp-instagram'
  | 'inapp-twitter'
  | 'inapp-linkedin'
  | 'inapp-threads'
  | 'unknown';

export type InstallMethod =
  | 'native-prompt'        // Desktop Chrome/Edge beforeinstallprompt
  | 'ios-safari'           // iOS Safari share → Add to Home Screen
  | 'ios-safari-26'        // iOS 26 Safari new flow
  | 'ios-chrome'           // iOS Chrome ⋯ → Add to Home Screen
  | 'android-chrome'       // Android Chrome ⋮ → Add to Home Screen
  | 'android-edge'         // Android Edge ⋯ → Add to Home Screen
  | 'android-firefox'      // Android Firefox ⋮ → Add to Home Screen
  | 'desktop-safari'       // Desktop Safari File → Add to Dock
  | 'open-in-browser'      // In-app browser → open in system browser first
  | 'not-supported';

export interface DeviceDetectionResult {
  device: DeviceType;
  browser: BrowserType;
  isStandalone: boolean;
  isIPad: boolean;
  installMethod: InstallMethod;
  canInstall: boolean;
  /** For Desktop Chrome/Edge — triggers the native install prompt */
  triggerNativeInstall: () => Promise<boolean>;
  /** Whether the native beforeinstallprompt event has been captured */
  hasNativePrompt: boolean;
}

// ─── LocalStorage key for "dismissed" tracking ───────────────────────────────

const STORAGE_KEY = 'atlas-a2hs-dismissed';
const STORAGE_COUNT_KEY = 'atlas-a2hs-count';

export function getA2HSDismissedTimestamp(): number | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

export function setA2HSDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    const count = getA2HSDisplayCount();
    localStorage.setItem(STORAGE_COUNT_KEY, (count + 1).toString());
  } catch {}
}

export function getA2HSDisplayCount(): number {
  try {
    const v = localStorage.getItem(STORAGE_COUNT_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

export function clearA2HSDismissed(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_COUNT_KEY);
  } catch {}
}

/**
 * Returns true if the prompt should be shown based on timing rules:
 * - First 2 dismissals: show again after 3 days
 * - After that: show again after 14 days
 * - Max 5 total shows
 */
export function shouldShowA2HSPrompt(): boolean {
  const count = getA2HSDisplayCount();
  if (count >= 5) return false;

  const ts = getA2HSDismissedTimestamp();
  if (!ts) return true; // never dismissed

  const daysSince = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  if (count <= 2) return daysSince >= 3;
  return daysSince >= 14;
}

// ─── Detection helpers (adapted from philfung/add-to-homescreen) ─────────────

function matchUA(regex: RegExp): boolean {
  return regex.test(navigator.userAgent);
}

function isDeviceIOS(): boolean {
  return (
    matchUA(/iPhone|iPad|iPod/) ||
    // iPad Air/Pro reports as Macintosh
    (matchUA(/Macintosh/) && navigator.maxTouchPoints > 1)
  );
}

function isDeviceAndroid(): boolean {
  return matchUA(/Android/);
}

function isIPad(): boolean {
  return (
    matchUA(/iPad/) ||
    (matchUA(/Macintosh/) && navigator.maxTouchPoints > 1)
  );
}

// iOS browsers
function isBrowserIOSChrome(): boolean {
  return isDeviceIOS() && matchUA(/CriOS/);
}
function isBrowserIOSFirefox(): boolean {
  return isDeviceIOS() && matchUA(/FxiOS/);
}
function isBrowserIOSInAppFacebook(): boolean {
  return isDeviceIOS() && matchUA(/FBAN|FBAV/);
}
function isBrowserIOSInAppLinkedin(): boolean {
  return isDeviceIOS() && matchUA(/LinkedInApp/);
}
function isBrowserIOSInAppInstagram(): boolean {
  if (!isDeviceIOS()) return false;
  try {
    return !!document.referrer.match('//l.instagram.com/');
  } catch {
    return false;
  }
}
function isBrowserIOSInAppTwitter(): boolean {
  if (!isDeviceIOS()) return false;
  try {
    return !!document.referrer.match('//t.co/');
  } catch {
    return false;
  }
}
function isBrowserIOSSafari(): boolean {
  return (
    isDeviceIOS() &&
    matchUA(/Safari/) &&
    !isBrowserIOSChrome() &&
    !isBrowserIOSFirefox() &&
    !isBrowserIOSInAppFacebook() &&
    !isBrowserIOSInAppLinkedin() &&
    !isBrowserIOSInAppInstagram() &&
    !isBrowserIOSInAppTwitter()
  );
}
function isBrowserIOSSafari26(): boolean {
  if (!isBrowserIOSSafari()) return false;
  const m = navigator.userAgent.match(/Version\/(\d+)/);
  if (m) return parseInt(m[1]) >= 26;
  return false;
}

// Android browsers
function isBrowserAndroidChrome(): boolean {
  return (
    isDeviceAndroid() &&
    matchUA(/Chrome/) &&
    !matchUA(/FBAN|FBAV/) &&
    !matchUA(/Instagram/) &&
    !matchUA(/SamsungBrowser/) &&
    !matchUA(/Firefox/) &&
    !matchUA(/Edg/) &&
    !matchUA(/OPR/)
  );
}
function isBrowserAndroidFacebook(): boolean {
  return isDeviceAndroid() && matchUA(/FBAN|FBAV/);
}
function isBrowserAndroidInstagram(): boolean {
  return isDeviceAndroid() && matchUA(/Instagram/);
}
function isBrowserAndroidFirefox(): boolean {
  return isDeviceAndroid() && matchUA(/Firefox/);
}
function isBrowserAndroidEdge(): boolean {
  return isDeviceAndroid() && matchUA(/Edg/);
}

// Desktop browsers
function isDesktopChrome(): boolean {
  return (
    navigator.userAgent.includes('Chrome') &&
    !navigator.userAgent.includes('Edg') &&
    (navigator.userAgent.includes('Windows') ||
      navigator.userAgent.includes('Macintosh') ||
      navigator.userAgent.includes('Linux')) &&
    !isDeviceIOS() &&
    !isDeviceAndroid()
  );
}
function isDesktopEdge(): boolean {
  return (
    navigator.userAgent.includes('Edg/') &&
    !isDeviceIOS() &&
    !isDeviceAndroid()
  );
}
function isDesktopSafari(): boolean {
  return (
    navigator.userAgent.includes('Safari') &&
    !navigator.userAgent.includes('Chrome') &&
    !navigator.userAgent.includes('Edg') &&
    navigator.userAgent.includes('Macintosh') &&
    !isDeviceIOS()
  );
}

// Standalone detection
function checkIsStandalone(): boolean {
  return (
    !!((navigator as any).standalone) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

// ─── Resolve browser type ────────────────────────────────────────────────────

function detectBrowser(): BrowserType {
  if (isDeviceIOS()) {
    if (isBrowserIOSInAppFacebook()) return 'inapp-facebook';
    if (isBrowserIOSInAppInstagram()) return 'inapp-instagram';
    if (isBrowserIOSInAppTwitter()) return 'inapp-twitter';
    if (isBrowserIOSInAppLinkedin()) return 'inapp-linkedin';
    if (isBrowserIOSSafari26()) return 'safari-26';
    if (isBrowserIOSSafari()) return 'safari';
    if (isBrowserIOSChrome()) return 'chrome';
    if (isBrowserIOSFirefox()) return 'firefox';
    return 'unknown';
  }
  if (isDeviceAndroid()) {
    if (isBrowserAndroidFacebook()) return 'inapp-facebook';
    if (isBrowserAndroidInstagram()) return 'inapp-instagram';
    if (isBrowserAndroidChrome()) return 'chrome';
    if (isBrowserAndroidEdge()) return 'edge';
    if (isBrowserAndroidFirefox()) return 'firefox';
    return 'unknown';
  }
  // Desktop
  if (isDesktopChrome()) return 'chrome';
  if (isDesktopEdge()) return 'edge';
  if (isDesktopSafari()) return 'safari';
  return 'unknown';
}

function detectDevice(): DeviceType {
  if (isDeviceIOS()) return 'ios';
  if (isDeviceAndroid()) return 'android';
  return 'desktop';
}

// ─── Resolve install method ──────────────────────────────────────────────────

function resolveInstallMethod(device: DeviceType, browser: BrowserType): InstallMethod {
  // In-app browsers → must open in system browser first
  if (browser.startsWith('inapp-')) return 'open-in-browser';

  if (device === 'ios') {
    if (browser === 'safari-26') return 'ios-safari-26';
    if (browser === 'safari') return 'ios-safari';
    if (browser === 'chrome') return 'ios-chrome';
    return 'not-supported';
  }

  if (device === 'android') {
    if (browser === 'chrome') return 'android-chrome';
    if (browser === 'edge') return 'android-edge';
    if (browser === 'firefox') return 'android-firefox';
    return 'not-supported';
  }

  // Desktop
  if (browser === 'chrome' || browser === 'edge') return 'native-prompt';
  if (browser === 'safari') return 'desktop-safari';
  return 'not-supported';
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useDeviceDetection(): DeviceDetectionResult {
  const device = detectDevice();
  const browser = detectBrowser();
  const isStandalone = checkIsStandalone();
  const iPad = isIPad();
  const installMethod = resolveInstallMethod(device, browser);
  const canInstall = installMethod !== 'not-supported' && !isStandalone;

  // beforeinstallprompt for Desktop Chrome/Edge
  const deferredPromptRef = useRef<any>(null);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);

  useEffect(() => {
    if (installMethod !== 'native-prompt') return;

    // Check if the event was captured globally before React mounted (see main.tsx)
    const earlyPrompt = (window as any).__pwaInstallPrompt;
    if (earlyPrompt) {
      deferredPromptRef.current = earlyPrompt;
      setHasNativePrompt(true);
      delete (window as any).__pwaInstallPrompt;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setHasNativePrompt(true);
      // Clear global ref since hook now owns it
      delete (window as any).__pwaInstallPrompt;
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [installMethod]);

  const triggerNativeInstall = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;

    prompt.prompt();
    const result = await prompt.userChoice;
    deferredPromptRef.current = null;
    setHasNativePrompt(false);
    return result.outcome === 'accepted';
  }, []);

  return {
    device,
    browser,
    isStandalone,
    isIPad: iPad,
    installMethod,
    canInstall,
    triggerNativeInstall,
    hasNativePrompt,
  };
}
